import { useState } from 'react'
import { ChevronDown, ChevronRight } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { countLabel, pathOf } from '@/lib/format'
import type { CrawledPage } from '@/types'

/** Collapsible, and open by default when there is anything to see. */
export function CrawlErrorsBand({
  errors,
  onRetry,
  onRetryAll,
  busy = false,
}: {
  errors: CrawledPage[]
  onRetry: (page: CrawledPage) => void
  onRetryAll: () => void
  busy?: boolean
}) {
  const [open, setOpen] = useState(errors.length > 0)

  if (errors.length === 0) return null

  return (
    <Card className="overflow-hidden">
      <div className="flex items-center gap-2 border-b border-rule px-4 py-3">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          className="flex items-center gap-1.5 text-left"
        >
          {open ? <ChevronDown size={14} aria-hidden /> : <ChevronRight size={14} aria-hidden />}
          <h2 className="text-[15px] leading-6">
            Errors <span className="tabular text-brand-red-700">({errors.length})</span>
          </h2>
        </button>
        <Button size="sm" className="ml-auto" loading={busy} onClick={onRetryAll}>
          Retry all {countLabel(errors.length, 'page')}
        </Button>
      </div>

      {open && (
        <ul className="divide-y divide-rule">
          {errors.map((page) => (
            <li key={page.id} className="flex flex-wrap items-center gap-3 px-4 py-2.5">
              <span className="min-w-0 flex-1">
                <span className="block truncate font-mono text-[12px] text-ink-muted" title={page.url}>
                  {pathOf(page.url)}
                </span>
                <span className="block text-[12px] text-brand-red-700">{page.error}</span>
              </span>
              <Button size="sm" onClick={() => onRetry(page)}>
                Retry
              </Button>
            </li>
          ))}
        </ul>
      )}
    </Card>
  )
}
